import { CreateTicketBrandings } from '@copetence/shared';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type ContextType = {
    branding: CreateTicketBrandings;
    showPoweredBy: boolean;
    appId: string;
    hideHeader: boolean;
    hideApplicationField: boolean;
    hideLanguageSelector: boolean;
    hideContactDetailsIfValid: boolean;
    hideFileUploads: boolean;
    hidePrioritySelector: boolean;
    hideCategorySelector: boolean;
    email: string | null;
    description: string | null;
    category: string | null;
    ref: string | null;
};

const ContextCtx = createContext<ContextType | undefined>(undefined);

export const ContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [searchParams] = useSearchParams();
    const [branding, setBranding] = useState<CreateTicketBrandings>(CreateTicketBrandings.COPETENCE);
    const [showPoweredBy, setShowPoweredBy] = useState<boolean>(true);
    const [hideHeader, setHideHeader] = useState<boolean>(false);
    const [hideApplicationField, setHideApplicationField] = useState<boolean>(false);
    const [hideLanguageSelector, setHideLanguageSelector] = useState<boolean>(false);
    const [hideContactDetailsIfValid, setHideContactDetailsIfValid] = useState<boolean>(false);
    const [hideFileUploads, setHideFileUploads] = useState<boolean>(false);
    const [hidePrioritySelector, setHidePrioritySelector] = useState<boolean>(false);
    const [hideCategorySelector, setHideCategorySelector] = useState<boolean>(false);
    const [appId, setAppId] = useState<string>("");
    const [email, setEmail] = useState<string | null>(null);
    const [description, setDescription] = useState<string | null>(null);
    const [category, setCategory] = useState<string | null>(null);
    const [ref, setRef] = useState<string | null>(null);

    useEffect(() => {
        const sp = searchParams;

        if (!sp) return;

        const sp_branding = sp.get("branding");
        const sp_showPoweredBy = sp.get("showPoweredBy");
        const sp_hideHeader = sp.get("hideHeader");
        const sp_hideLanguageSelector = sp.get("hideLanguageSelector");
        const sp_hideApp = sp.get("hideApp");
        const sp_hideContactDetailsIfValid = sp.get("hideContactDetailsIfValid");
        const sp_hideFileUploads = sp.get("hideFileUploads");
        const sp_hidePrioritySelector = sp.get("hidePrioritySelector");
        const sp_hideCategorySelector = sp.get("hideCategorySelector");
        const sp_app = sp.get("app");
        const sp_email = sp.get("email");
        const sp_description = sp.get("description");
        const sp_category = sp.get("category");
        const sp_ref = sp.get("ref");

        if (sp_email) setEmail(sp_email);
        if (sp_description) setDescription(sp_description);
        if (sp_category) setCategory(sp_category);
        if (sp_ref) setRef(decodeURIComponent(sp_ref) || null);

        if (sp_branding && Object.values(CreateTicketBrandings).includes(sp_branding as CreateTicketBrandings)) {
            setBranding(sp_branding as CreateTicketBrandings);
            document.body.className = `branding__${sp_branding}`;
        } else {
            // Default branding: coPetence
            setBranding(CreateTicketBrandings.COPETENCE);
            document.body.className = `branding__${CreateTicketBrandings.COPETENCE}`;
        }

        if (sp_showPoweredBy) setShowPoweredBy(sp_showPoweredBy === "true");
        if (sp_hideHeader) setHideHeader(sp_hideHeader === "true");
        if (sp_hideApp) setHideApplicationField(sp_hideApp === "true");
        if (sp_hideLanguageSelector) setHideLanguageSelector(sp_hideLanguageSelector === "true");
        if (sp_hideContactDetailsIfValid) setHideContactDetailsIfValid(sp_hideContactDetailsIfValid === "true");
        if (sp_hideFileUploads) setHideFileUploads(sp_hideFileUploads === "true");
        if (sp_hidePrioritySelector) setHidePrioritySelector(sp_hidePrioritySelector === "true");
        if (sp_hideCategorySelector) setHideCategorySelector(sp_hideCategorySelector === "true");
        if (sp_app) setAppId(sp_app);
        
        // URL decode the email
        if (sp_email) setEmail(decodeURIComponent(sp_email) || null);
        // URL decode the description
        if (sp_description) setDescription(decodeURIComponent(sp_description) || null);
        // URL decode the category
        if (sp_category) setCategory(decodeURIComponent(sp_category) || null);

        // write email to local storage
        
        if (!sp_email) {
            const enq_email = localStorage.getItem("enq_email");
            if (enq_email) setEmail(enq_email);
        } else {
            localStorage.setItem("enq_email", sp_email);
        }

    }, [searchParams]);


    return (
        <ContextCtx.Provider value={{
            branding,
            showPoweredBy,
            appId,
            hideHeader,
            hideApplicationField,
            hideLanguageSelector,
            hideContactDetailsIfValid,
            hideFileUploads,
            hidePrioritySelector,
            hideCategorySelector,
            email,
            description,
            category,
            ref,
        }}>
            {children}
        </ContextCtx.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(ContextCtx);
    if (!context) {
        throw new Error('useAppContext must be used within a ContextProvider');
    }
    return context;
};